import React, { createContext, useContext, ReactNode } from 'react';
import {
  API_URL,
  ENVIRONMENT,
  VERSION,
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  ORACLE_BUCKET_REGION,
  ORACLE_BUCKET_NAMESPACE,
  ORACLE_BUCKET_NAME,
  MAX_PHOTO_SIZE_BYTES,
  ORACLE_BUCKET_PAR,
  MAX_RETRY_ATTEMPTS,
  STEP_TRANSITION_DELAY_MS,
  MIN_PHONE_NUMBER_LENGTH,
  MIN_PASSWORD_LENGTH,
  MIN_PHOTOS_REQUIRED,
  MAX_PHOTOS_ALLOWED
} from '../constants';

export interface SupabaseConfig {
  url: string;
  publishableKey: string;
}

export interface OracleBucketConfig {
  region: string;
  namespace: string;
  bucketName: string;
  maxPhotoSize: number;
  par: string;
}

export interface AppConstants {
  maxRetryAttempts?: number;
  stepTransitionDelayMs?: number;
  minPhoneNumberLength?: number;
  minPasswordLength?: number;
  minPhotosRequired?: number;
  maxPhotosAllowed?: number;
}

export interface Config {
  apiUrl: string;
  environment: string;
  version: string;
  supabase?: SupabaseConfig;
  oracleBucket?: OracleBucketConfig;
  constants?: AppConstants;
}

export interface ConfigContextType {
  config: Config;
  loading: boolean;
  error: string | null;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

interface ConfigProviderProps {
  children: ReactNode;
}

export function ConfigProvider({ children }: ConfigProviderProps) {
  // Build config object from individual constants
  const config: Config = {
    apiUrl: API_URL,
    environment: ENVIRONMENT,
    version: VERSION,
    supabase: SUPABASE_URL && SUPABASE_PUBLISHABLE_KEY ? {
      url: SUPABASE_URL,
      publishableKey: SUPABASE_PUBLISHABLE_KEY
    } : undefined,
    oracleBucket: ORACLE_BUCKET_REGION && ORACLE_BUCKET_NAMESPACE && ORACLE_BUCKET_NAME ? {
      region: ORACLE_BUCKET_REGION,
      namespace: ORACLE_BUCKET_NAMESPACE,
      bucketName: ORACLE_BUCKET_NAME,
      maxPhotoSize: MAX_PHOTO_SIZE_BYTES,
      par: ORACLE_BUCKET_PAR
    } : undefined,
    constants: {
      maxRetryAttempts: MAX_RETRY_ATTEMPTS,
      stepTransitionDelayMs: STEP_TRANSITION_DELAY_MS,
      minPhoneNumberLength: MIN_PHONE_NUMBER_LENGTH,
      minPasswordLength: MIN_PASSWORD_LENGTH,
      minPhotosRequired: MIN_PHOTOS_REQUIRED,
      maxPhotosAllowed: MAX_PHOTOS_ALLOWED
    }
  };

  const loading = false;
  const error = null;

  return (
    <ConfigContext.Provider value={{ config, loading, error }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}
